import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppBar, Tab, Tabs, Tooltip } from '@material-ui/core';
import classNames from 'classnames';

import { useByggMoterom } from '@klp/bygg/hooks/useByggMoterom';
import type { IClient } from '@klp/client';
import { Illustration, IconName } from '@klp/design/Illustrations';
import { FakturaAccess } from '@klp/faktura/config';
import { SakAccess } from '@klp/sak/config';
import {
    MiljoprofilAccess,
    MiljoprofilRoutes,
} from '@klp/sustainability/config';
import type { IUser } from '@klp/user/interfaces';

import { BuildingTabs } from '../../classes';
import { ByggCalendarAccess, ByggRoutes } from '../../config';
import { ByggKundeAccess } from '../ByggKunder/config';

type Props = {
    byggNummer: string | undefined;
    tab: string | undefined;
    user: IUser | undefined;
    selectedClient?: IClient | undefined;
    isAdmin?: boolean;
    classes: Record<string, string>;
};

export const ByggSingleTabs = ({
    byggNummer,
    tab,
    user,
    isAdmin,
    classes,
}: Props) => {
    const { t } = useTranslation();
    const activeTab = useMemo(
        () =>
            tab && Object.values(BuildingTabs).includes(tab as BuildingTabs)
                ? tab
                : BuildingTabs.overview,
        [tab],
    );

    const { byggMoteromList } = useByggMoterom(byggNummer);

    return (
        <AppBar position="relative" className={classes.appBar}>
            <Tabs
                indicatorColor="secondary"
                textColor="secondary"
                variant="scrollable"
                orientation="horizontal"
                scrollButtons="on"
                value={activeTab}
            >
                <Tab
                    label={t('bygg.overview')}
                    component={Link}
                    to={ByggRoutes.single(byggNummer, BuildingTabs.overview)}
                    value={BuildingTabs.overview}
                    className={classes.tab}
                />
                <Tab
                    label={t('byggCalendar.name')}
                    component={Link}
                    to={ByggRoutes.single(byggNummer, BuildingTabs.calendar)}
                    value={BuildingTabs.calendar}
                    disabled={byggMoteromList?.length === 0}
                    className={classNames(classes.tab, {
                        [classes.tabHidden]: !ByggCalendarAccess(user),
                    })}
                />

                <Tab
                    label={t('bygg.documents')}
                    component={Link}
                    to={ByggRoutes.single(byggNummer, BuildingTabs.documents)}
                    value={BuildingTabs.documents}
                    className={classes.tab}
                />
                <Tab
                    label={t('faktura.faktura')}
                    component={Link}
                    to={ByggRoutes.single(byggNummer, BuildingTabs.invoices)}
                    value={BuildingTabs.invoices}
                    className={classNames(classes.tab, {
                        [classes.tabHidden]: !FakturaAccess(user),
                    })}
                />

                <Tab
                    label={t('bygg.forbruk')}
                    component={Link}
                    to={MiljoprofilRoutes.list(byggNummer)}
                    value={BuildingTabs.consumption}
                    className={classNames(classes.tab, {
                        [classes.tabHidden]: !MiljoprofilAccess(),
                    })}
                />

                <Tab
                    label={
                        <Tooltip title={t('general.adminContent')}>
                            <div className={classes.iconTextContainer}>
                                <Illustration
                                    icon={IconName.ADMIN}
                                    className={classes.iconSpacing}
                                />
                                {t('bygg.customers')}
                            </div>
                        </Tooltip>
                    }
                    component={Link}
                    to={ByggRoutes.single(byggNummer, BuildingTabs.customers)}
                    value={BuildingTabs.customers}
                    className={classNames(classes.tab, {
                        [classes.tabHidden]: !ByggKundeAccess(user),
                    })}
                />

                <Tab
                    label={
                        <Tooltip title={t('general.adminContent')}>
                            <div className={classes.iconTextContainer}>
                                <Illustration
                                    icon={IconName.ADMIN}
                                    className={classes.iconSpacing}
                                />
                                {t('bygg.tickets')}
                            </div>
                        </Tooltip>
                    }
                    component={Link}
                    to={ByggRoutes.single(byggNummer, BuildingTabs.tickets)}
                    value={BuildingTabs.tickets}
                    className={classNames(classes.tab, {
                        [classes.tabHidden]: !SakAccess(user) || !isAdmin,
                    })}
                />
            </Tabs>
        </AppBar>
    );
};
